import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import authService from "../../auth/services/authService";
import { getWebSocketUrl, getAuthenticatedAxios } from "../../../utils/api";
import useSearchProgress from "./useSearchProgress";
import {
  getPaperAnalysisGuidance,
  requestPaperAnalysisGuidance,
} from "../services/paperAnalysisGuidanceService";

export const SEARCH_ERROR_MESSAGE_KEY = "app.errors.searchFailed";
export const WEBSOCKET_ERROR_MESSAGE_KEY = "app.errors.websocketConnection";

export const DEFAULT_SEARCH_ERROR_MESSAGE = "Search failed. Please try again.";
const WORKFLOW_CONFIRMATION_TIMEOUT_MESSAGE = "Workflow confirmation timeout";
const WORKFLOW_CONFIRMATION_TIMEOUT_MS = 5 * 60 * 1000;

const WORKFLOW_ERROR_CODE_MESSAGE_KEYS = {
  invalid_json: "app.errors.invalidPayload",
  invalid_payload: "app.errors.invalidPayload",
  invalid_confirmation_payload: "app.errors.criteriaConfirmationInvalid",
  unsupported_event_type: "app.errors.workflowUnsupportedEvent",
  workflow_resume_failed: SEARCH_ERROR_MESSAGE_KEY,
  workflow_start_failed: SEARCH_ERROR_MESSAGE_KEY,
  connection_error: WEBSOCKET_ERROR_MESSAGE_KEY,
};

const WORKFLOW_PROMPT_MESSAGE_TYPES = [
  "criteria_confirmation",
  "purpose_collection",
  "purpose_clarification",
  "query_clarification",
];
const WORKFLOW_INTERMEDIATE_MESSAGE_TYPES = new Set([
  ...WORKFLOW_PROMPT_MESSAGE_TYPES,
  "purpose_user_reply",
]);

const getPaperKey = (paper) =>
  paper?.pmid || paper?.id || paper?.url || paper?.title || null;

const mergePaperList = (existing = [], incoming = []) => {
  const byKey = new Map();
  existing.forEach((paper) => {
    const key = getPaperKey(paper);
    if (key) {
      byKey.set(key, { ...paper });
    }
  });

  incoming.forEach((paper) => {
    const key = getPaperKey(paper);
    if (!key) {
      return;
    }
    byKey.set(key, {
      ...(byKey.get(key) || {}),
      ...paper,
    });
  });

  return Array.from(byKey.values());
};

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const resolvePaperRank = (paper = {}) =>
  toFiniteNumber(
    paper.rank ??
      paper.ranking ??
      paper.Rank ??
      paper.Ranking ??
      null,
  );

const sortPapersByRanking = (papers = []) =>
  [...papers].sort((a, b) => {
    const rankA = resolvePaperRank(a);
    const rankB = resolvePaperRank(b);

    if (rankA !== null && rankB !== null && rankA !== rankB) {
      return rankA - rankB;
    }
    if (rankA !== null && rankB === null) {
      return -1;
    }
    if (rankA === null && rankB !== null) {
      return 1;
    }

    const scoreA = toFiniteNumber(a?.relevance_score) ?? 0;
    const scoreB = toFiniteNumber(b?.relevance_score) ?? 0;
    if (scoreA !== scoreB) {
      return scoreB - scoreA;
    }

    return String(a?.title || "").localeCompare(String(b?.title || ""));
  });

export const normalizeEngineErrorMessage = (
  error,
  fallbackMessage = DEFAULT_SEARCH_ERROR_MESSAGE,
) => {
  if (error?.response?.data?.detail) {
    return typeof error.response.data.detail === "string"
      ? error.response.data.detail
      : JSON.stringify(error.response.data.detail);
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

export const safeParseWebSocketMessage = (rawData) => {
  if (!rawData) return null;
  if (typeof rawData === "object") return rawData;
  if (typeof rawData !== "string") return null;

  try {
    const parsed = JSON.parse(rawData);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_error) {
    return null;
  }
};

export const buildSearchRequestPayload = ({
  searchQuery,
  maxResults,
  language,
  searchMode,
}) => ({
  query: searchQuery,
  max_results: maxResults,
  language,
  search_mode: searchMode,
  workflow_mode: "search",
});

const resolveWorkflowErrorMessage = ({ data, t, fallbackMessage }) => {
  const messageKey =
    data?.code && WORKFLOW_ERROR_CODE_MESSAGE_KEYS[data.code]
      ? WORKFLOW_ERROR_CODE_MESSAGE_KEYS[data.code]
      : null;
  if (messageKey) {
    const translated = t(messageKey);
    if (translated && translated !== messageKey) {
      return translated;
    }
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  return fallbackMessage;
};

export const useSearchEngine = ({
  setMessages,
  setCurrentSearchId,
  setHasSearchedInSession,
  updateUsage,
  hasSubmittedSurvey,
  onSearchComplete,
}) => {
  const { t, i18n } = useTranslation();
  const searchErrorFallback = t(SEARCH_ERROR_MESSAGE_KEY);
  const websocketErrorFallback = t(WEBSOCKET_ERROR_MESSAGE_KEY);

  const [query, setQuery] = useState("");
  const [searchMode, setSearchModeState] = useState(() => {
    if (typeof window === "undefined") {
      return "basic";
    }
    return window.localStorage.getItem("pubmed-search-mode") === "pro"
      ? "pro"
      : "basic";
  });
  const [ws, setWs] = useState(null);
  const [discoveredPapers, setDiscoveredPapers] = useState(null);
  const [isAwaitingCriteriaConfirmation, setIsAwaitingCriteriaConfirmation] = useState(false);
  const [isAwaitingUserPurpose, setIsAwaitingUserPurpose] = useState(false);
  const [workflowWaitingContext, setWorkflowWaitingContext] = useState(null);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [paperAnalysisGuidance, setPaperAnalysisGuidance] = useState(null);
  const wsRef = useRef(null);
  const searchLifecycleRef = useRef(0);
  const workflowContextRef = useRef({ runId: null, threadId: null });
  const pendingWorkflowRef = useRef(new Map());
  const pendingSearchQueryRef = useRef("");
  const pendingSearchModeRef = useRef("basic");
  const guidanceRequestedRunIdsRef = useRef(new Set());

  const {
    progress,
    resetProgress,
    startStage,
    startStageWithCompletedHistory,
    handleStatusUpdate,
    handleProgressUpdate,
    handleIntermediateResult,
    handleComplete: handleProgressComplete,
    handleError: handleProgressError,
    SearchStages,
  } = useSearchProgress();

  const resolvePendingWorkflow = useCallback((runId, resolverType, value) => {
    const pending = pendingWorkflowRef.current.get(runId);
    if (!pending) {
      return;
    }

    if (pending.timeoutId) {
      clearTimeout(pending.timeoutId);
    }
    pendingWorkflowRef.current.delete(runId);

    if (resolverType === "resolve") {
      pending.resolve(value);
      return;
    }
    pending.reject(value);
  }, []);

  const waitForWorkflowResult = useCallback((runId, timeoutMs = WORKFLOW_CONFIRMATION_TIMEOUT_MS) => {
    return new Promise((resolve, reject) => {
      let timeoutId = null;
      if (typeof timeoutMs === "number" && timeoutMs > 0) {
        timeoutId = setTimeout(() => {
          pendingWorkflowRef.current.delete(runId);
          reject(new Error(WORKFLOW_CONFIRMATION_TIMEOUT_MESSAGE));
        }, timeoutMs);
      }

      pendingWorkflowRef.current.set(runId, {
        resolve,
        reject,
        timeoutId,
      });
    });
  }, []);

  const waitForWorkflowResultWhileSocketAlive = useCallback(
    (runId, websocket) =>
      new Promise((resolve, reject) => {
        if (!websocket || websocket.readyState !== WebSocket.OPEN) {
          reject(new Error("WebSocket is closed"));
          return;
        }

        let settled = false;

        const cleanup = () => {
          websocket.removeEventListener("close", onClose);
          websocket.removeEventListener("error", onError);
        };

        const settleResolve = (value) => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(value);
        };

        const settleReject = (error) => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(error);
        };

        const onClose = () => {
          settleReject(new Error("WebSocket closed while waiting for workflow result"));
        };

        const onError = () => {
          settleReject(new Error("WebSocket error while waiting for workflow result"));
        };

        websocket.addEventListener("close", onClose, { once: true });
        websocket.addEventListener("error", onError, { once: true });

        waitForWorkflowResult(runId, null).then(settleResolve).catch(settleReject);
      }),
    [waitForWorkflowResult],
  );

  const upsertCriteriaMessage = useCallback(
    (criteriaMessage) => {
      setMessages((prev) => {
        const idx = prev.findIndex(
          (message) =>
            message.type === "criteria_confirmation" &&
            message.runId === criteriaMessage.runId,
        );

        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            ...criteriaMessage,
          };
          return next;
        }

        return [...prev, criteriaMessage];
      });
    },
    [setMessages],
  );

  const upsertPurposeMessage = useCallback(
    (purposeMessage) => {
      setMessages((prev) => {
        const idx = prev.findIndex(
          (message) =>
            message.type === purposeMessage.type &&
            message.runId === purposeMessage.runId,
        );

        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            ...purposeMessage,
          };
          return next;
        }

        return [...prev, purposeMessage];
      });
    },
    [setMessages],
  );

  const upsertPurposeReplyMessage = useCallback(
    (purposeReplyMessage) => {
      setMessages((prev) => {
        const idx = prev.findIndex(
          (message) =>
            message.type === "purpose_user_reply" &&
            message.runId === purposeReplyMessage.runId,
        );

        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            ...purposeReplyMessage,
          };
          return next;
        }

        return [...prev, purposeReplyMessage];
      });
    },
    [setMessages],
  );

  const upsertQueryMessage = useCallback(
    (queryMessage) => {
      setMessages((prev) => {
        const idx = prev.findIndex(
          (message) =>
            message.type === "query_clarification" &&
            message.runId === queryMessage.runId,
        );

        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            ...queryMessage,
          };
          return next;
        }

        return [...prev, queryMessage];
      });
    },
    [setMessages],
  );

  const lockWorkflowPromptMessages = useCallback(
    ({ runId, types }) => {
      if (!runId) return;
      const targetTypes = Array.isArray(types) && types.length > 0 ? types : WORKFLOW_PROMPT_MESSAGE_TYPES;
      setMessages((prev) =>
        prev.map((message) => {
          if (!targetTypes.includes(message.type) || message.runId !== runId) {
            return message;
          }
          return {
            ...message,
            pending: false,
            error: null,
            locked: true,
          };
        }),
      );
    },
    [setMessages],
  );

  const applyInterruptPayload = useCallback(
    ({ runId, threadId, payload }) => {
      if (!runId || !payload || typeof payload !== "object") {
        return;
      }

      if (payload.type === "criteria_confirmation") {
        setWorkflowWaitingContext(null);
        lockWorkflowPromptMessages({
          runId,
          types: ["purpose_collection", "purpose_clarification"],
        });
        upsertCriteriaMessage({
          id: `criteria-${runId}`,
          type: "criteria_confirmation",
          runId,
          threadId,
          criteriaDraft: payload.criteria_draft || [],
          reasoning: payload.reasoning || "",
          promptMessage: payload.message || t("app.criteriaConfirmation.message"),
          pending: false,
          error: null,
        });
        setIsAwaitingCriteriaConfirmation(true);
        setIsAwaitingUserPurpose(false);
        return;
      }

      if (payload.type === "query_clarification") {
        setWorkflowWaitingContext(null);
        lockWorkflowPromptMessages({
          runId,
          types: [
            "purpose_collection",
            "purpose_clarification",
            "criteria_confirmation",
          ],
        });
        upsertQueryMessage({
          id: `query-${runId}`,
          type: "query_clarification",
          runId,
          threadId,
          queryDraft: payload.query_draft || [],
          promptMessage: t("app.queryPrompt.message"),
          helperText: t("app.queryPrompt.helper"),
          pending: false,
          error: null,
        });
        setIsAwaitingCriteriaConfirmation(true);
        setIsAwaitingUserPurpose(false);
        return;
      }

      if (payload.type === "purpose_collection" || payload.type === "purpose_clarification") {
        setWorkflowWaitingContext(null);
        if (payload.type === "purpose_clarification") {
          lockWorkflowPromptMessages({
            runId,
            types: ["purpose_collection"],
          });
        }
        const fallbackHelperKey =
          payload.type === "purpose_clarification"
            ? "app.purposePrompt.clarificationHelper"
            : "app.purposePrompt.helper";
        const promptMessage =
          payload.type === "purpose_collection"
            ? t("app.purposePrompt.message")
            : t("app.purposePrompt.clarificationMessage");

        upsertPurposeMessage({
          id: `${payload.type}-${runId}`,
          type: payload.type,
          runId,
          threadId,
          promptMessage,
          helperText: t(fallbackHelperKey),
          placeholder: t("app.purposePrompt.placeholder"),
          routeLabel:
            payload.type === "purpose_collection" ? payload.route_label || "" : "",
          purposeOptions:
            payload.type === "purpose_collection" && Array.isArray(payload.options)
              ? payload.options
              : [],
          draftPurpose:
            payload.type === "purpose_clarification" ? payload.draft_purpose || "" : "",
          pending: false,
          error: null,
        });
        setIsAwaitingCriteriaConfirmation(true);
        setIsAwaitingUserPurpose(true);
      }
    },
    [
      lockWorkflowPromptMessages,
      setIsAwaitingCriteriaConfirmation,
      setIsAwaitingUserPurpose,
      setWorkflowWaitingContext,
      t,
      upsertCriteriaMessage,
      upsertPurposeMessage,
      upsertQueryMessage,
    ],
  );

  const handleWorkflowResult = useCallback(
    ({ payload, runId }) => {
      handleProgressComplete(payload);
      setDiscoveredPapers(null);
      setIsAwaitingCriteriaConfirmation(false);
      setIsAwaitingUserPurpose(false);
      setWorkflowWaitingContext(null);

      if (runId) {
        workflowContextRef.current.runId = runId;
        setCurrentSearchId(runId);
      }

      setMessages((prev) => {
        const sortedPapers = sortPapersByRanking(payload?.papers || []);
        const overviewMessage = prev.find((message) => message.type === "ai_overview");
        const streamMessage = prev.find(
          (message) => message.type === "ai_overview_stream",
        );

        const filtered = prev.filter(
          (message) =>
            !WORKFLOW_INTERMEDIATE_MESSAGE_TYPES.has(message.type) &&
            message.type !== "ai_overview" &&
            message.type !== "ai_overview_stream",
        );

        return [
          ...filtered,
          {
            id: Date.now(),
            type: "result",
            papers: sortedPapers,
            query: pendingSearchQueryRef.current,
            summary: payload?.summary || "",
            queries_used: payload?.search_queries_used || [],
            total_found: payload?.total_pmids_found || 0,
            search_mode: pendingSearchModeRef.current,
            ai_overview:
              payload?.ai_overview ||
              overviewMessage?.data ||
              streamMessage?.content ||
              null,
            total_cost_usd: payload?.total_cost_usd || 0,
          },
        ];
      });

      if (onSearchComplete) {
        onSearchComplete(runId || null);
      }
    },
    [
      handleProgressComplete,
      onSearchComplete,
      setCurrentSearchId,
      setMessages,
      setWorkflowWaitingContext,
    ],
  );

  const requestGuidanceForRankedPapers = useCallback(
    async ({ runId, papers }) => {
      if (!runId || guidanceRequestedRunIdsRef.current.has(runId)) {
        return;
      }
      if (!Array.isArray(papers) || papers.length === 0) {
        return;
      }

      guidanceRequestedRunIdsRef.current.add(runId);
      setPaperAnalysisGuidance(null);

      try {
        const guidance = await requestPaperAnalysisGuidance({
          searchRunId: runId,
          query: pendingSearchQueryRef.current,
          language: i18n.language,
          searchMode: pendingSearchModeRef.current,
          papers: sortPapersByRanking(papers).slice(0, 10),
        });
        if (workflowContextRef.current.runId === runId) {
          setPaperAnalysisGuidance(guidance);
        }
      } catch (error) {
        console.error("Failed to generate paper analysis guidance:", error);
      }
    },
    [i18n.language],
  );

  const loadPersistedPaperAnalysisGuidance = useCallback(async (searchRunId) => {
    const resolvedSearchRunId = String(searchRunId || "").trim();
    if (!resolvedSearchRunId) {
      setPaperAnalysisGuidance(null);
      return null;
    }

    try {
      const guidance = await getPaperAnalysisGuidance({
        searchRunId: resolvedSearchRunId,
      });
      setPaperAnalysisGuidance(guidance);
      return guidance;
    } catch (error) {
      setPaperAnalysisGuidance(null);
      console.error("Failed to load paper analysis guidance:", error);
      throw error;
    }
  }, []);

  const handleWorkflowIntermediate = useCallback(
    (payload, runId = null) => {
      if (!payload?.stage) {
        return;
      }

      handleIntermediateResult(payload);

      if (payload.stage === SearchStages.FETCHING_DETAILS && payload.articles) {
        setDiscoveredPapers({
          papers: payload.articles,
          message: t("app.fetchingPapers", { count: payload.count || payload.articles.length }),
        });
        return;
      }

      if (payload.stage === SearchStages.ANALYZING_RESULTS && payload.analysis) {
        requestGuidanceForRankedPapers({
          runId: runId || workflowContextRef.current.runId,
          papers: payload.analysis,
        });
        setDiscoveredPapers((prev) => ({
          papers: mergePaperList(prev?.papers || [], payload.analysis || []),
          message: prev?.message,
        }));
        return;
      }

      if (
        payload.stage === SearchStages.EXTRACTING_STRUCTURED_FIELDS &&
        payload.extracted_fields
      ) {
        setDiscoveredPapers((prev) => {
          if (!prev?.papers?.length) {
            return prev;
          }
          const papers = prev.papers.map((paper) => {
            const paperId = String(paper.pmid || paper.id || "");
            const extracted = payload.extracted_fields[paperId];
            if (!extracted) {
              return paper;
            }
            return {
              ...paper,
              ...extracted,
            };
          });
          return {
            ...prev,
            papers,
          };
        });
        return;
      }

      if (payload.stage === SearchStages.EXTRACTING_IMAGES && payload.images) {
        setDiscoveredPapers((prev) => {
          if (!prev?.papers?.length) {
            return prev;
          }

          const papers = prev.papers.map((paper) => {
            const paperId = String(paper.pmid || paper.id || "");
            return {
              ...paper,
              image_urls: payload.images[paperId] || paper.image_urls || [],
            };
          });

          return {
            ...prev,
            papers,
          };
        });
        return;
      }

      if (payload.stage === SearchStages.OVERVIEW_STREAMING && payload.chunk) {
        setMessages((prev) => {
          const next = [...prev];
          const streamIndex = next.findIndex(
            (message) => message.type === "ai_overview_stream",
          );
          if (streamIndex >= 0) {
            next[streamIndex] = {
              ...next[streamIndex],
              content: `${next[streamIndex].content || ""}${payload.chunk}`,
            };
            return next;
          }

          next.push({
            id: Date.now(),
            type: "ai_overview_stream",
            content: payload.chunk,
          });
          return next;
        });
        return;
      }

      if (payload.stage === SearchStages.OVERVIEW_COMPLETE && payload.overview) {
        setMessages((prev) => {
          const filtered = prev.filter(
            (message) => message.type !== "ai_overview_stream",
          );
          return [
            ...filtered,
            {
              id: Date.now(),
              type: "ai_overview",
              data: payload.overview,
            },
          ];
        });
      }
    },
    [
      SearchStages.ANALYZING_RESULTS,
      SearchStages.EXTRACTING_IMAGES,
      SearchStages.EXTRACTING_STRUCTURED_FIELDS,
      SearchStages.FETCHING_DETAILS,
      SearchStages.OVERVIEW_COMPLETE,
      SearchStages.OVERVIEW_STREAMING,
      handleIntermediateResult,
      requestGuidanceForRankedPapers,
      setMessages,
      t,
    ],
  );

  const handleWorkflowError = useCallback(
    ({ data, runId }) => {
      const message = resolveWorkflowErrorMessage({
        data,
        t,
        fallbackMessage: searchErrorFallback,
      });
      setDiscoveredPapers(null);
      setIsAwaitingCriteriaConfirmation(false);
      setIsAwaitingUserPurpose(false);
      setWorkflowWaitingContext(null);
      handleProgressError({ message });
      if (runId) {
        setMessages((prev) =>
          prev.map((entry) => {
            if (WORKFLOW_PROMPT_MESSAGE_TYPES.includes(entry.type) && entry.runId === runId) {
              return {
                ...entry,
                pending: false,
                error: message,
              };
            }
            return entry;
          }),
        );
      }
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "error",
          content: message,
        },
      ]);
    },
    [handleProgressError, searchErrorFallback, setMessages, setWorkflowWaitingContext, t],
  );

  const handleWebSocketMessage = useCallback(
    (event) => {
      const data = safeParseWebSocketMessage(event?.data);
      if (!data?.type) {
        return;
      }

      const runId = data.run_id;

      try {
        if (data.type === "workflow_status") {
          if (runId && data.status === "running") {
            workflowContextRef.current.runId = runId;
            setCurrentSearchId(runId);
          }
          handleStatusUpdate(data);
          if (data.status === "failed" && runId) {
            resolvePendingWorkflow(
              runId,
              "reject",
              new Error(data.message || searchErrorFallback),
            );
          }
          return;
        }

        if (data.type === "workflow_progress") {
          handleProgressUpdate(data.payload || data);
          return;
        }

        if (data.type === "workflow_intermediate") {
          handleWorkflowIntermediate(data.payload || {}, runId);
          return;
        }

        if (data.type === "criteria_confirmation_required") {
          applyInterruptPayload({
            runId: data.run_id,
            threadId: data.thread_id,
            payload: data.payload || {},
          });
          if (runId) {
            resolvePendingWorkflow(runId, "resolve", {
              status: "criteria_confirmation_required",
              payload: data.payload || {},
            });
          }
          return;
        }

        if (data.type === "workflow_result") {
          handleWorkflowResult({ payload: data.payload || {}, runId });
          if (runId) {
            resolvePendingWorkflow(runId, "resolve", data.payload || {});
          }
          return;
        }

        if (data.type === "workflow_error") {
          handleWorkflowError({ data, runId });
          if (runId) {
            resolvePendingWorkflow(
              runId,
              "reject",
              new Error(
                resolveWorkflowErrorMessage({
                  data,
                  t,
                  fallbackMessage: searchErrorFallback,
                }),
              ),
            );
          }
          return;
        }

        if (data.type === "usage_limit_exceeded") {
          setIsAwaitingCriteriaConfirmation(false);
          setIsAwaitingUserPurpose(false);
          setWorkflowWaitingContext(null);
          if (data.current_usage !== undefined && data.limit !== undefined) {
            updateUsage(data.current_usage, data.limit);
          }
          if (!hasSubmittedSurvey) {
            setShowSurveyModal(true);
          }
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              type: "error",
              content: t("usage.limitReached"),
            },
          ]);
          return;
        }

        if (data.type === "usage_updated") {
          if (data.current_usage !== undefined) {
            updateUsage(data.current_usage, data.limit || 100);
          }
        }
      } catch (error) {
        const message = normalizeEngineErrorMessage(error, searchErrorFallback);
        setDiscoveredPapers(null);
        handleProgressError({ message });
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            type: "error",
            content: message,
          },
        ]);
      }
    },
    [
      applyInterruptPayload,
      handleProgressError,
      handleProgressUpdate,
      handleStatusUpdate,
      handleWorkflowError,
      handleWorkflowIntermediate,
      handleWorkflowResult,
      hasSubmittedSurvey,
      resolvePendingWorkflow,
      searchErrorFallback,
      setCurrentSearchId,
      setMessages,
      setIsAwaitingCriteriaConfirmation,
      setIsAwaitingUserPurpose,
      setWorkflowWaitingContext,
      t,
      updateUsage,
    ],
  );

  const connectWebSocket = useCallback(() => {
    const wsUrl = getWebSocketUrl("v2");
    const websocket = new WebSocket(wsUrl);
    const lifecycleId = searchLifecycleRef.current;

    wsRef.current = websocket;

    const isCurrentSocket = () =>
      searchLifecycleRef.current === lifecycleId && wsRef.current === websocket;

    websocket.onopen = () => {
      if (!isCurrentSocket()) {
        if (
          websocket.readyState !== WebSocket.CLOSING &&
          websocket.readyState !== WebSocket.CLOSED
        ) {
          websocket.close();
        }
        return;
      }
      setWs(websocket);
    };

    websocket.onmessage = (event) => {
      if (!isCurrentSocket()) return;
      handleWebSocketMessage(event);
    };

    websocket.onclose = () => {
      if (wsRef.current === websocket) {
        wsRef.current = null;
        setWs(null);
      }
    };

    websocket.onerror = () => {
      if (!isCurrentSocket()) return;
      handleProgressError({ message: websocketErrorFallback });
    };

    return websocket;
  }, [handleProgressError, handleWebSocketMessage, websocketErrorFallback]);

  const setSearchMode = useCallback((nextMode) => {
    const resolved = nextMode === "pro" ? "pro" : "basic";
    setSearchModeState(resolved);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("pubmed-search-mode", resolved);
    }
  }, []);

  const refreshUsageSnapshot = useCallback(async () => {
    try {
      const authAxios = getAuthenticatedAxios();
      const response = await authAxios.get("/api/v1/usage/current");
      const current = response?.data?.data?.current_usage;
      const limit = response?.data?.data?.limit;
      if (typeof current === "number") {
        updateUsage(current, limit);
      }
    } catch (_error) {
      // Keep search results even if usage refresh fails.
    }
  }, [updateUsage]);

  const shouldRefreshTokenForWebSocket = useCallback(() => {
    if (typeof window !== "undefined" && window.location.protocol !== "https:") {
      return false;
    }

    const token = authService.getAccessToken();
    if (!token) return false;

    const parts = token.split(".");
    if (parts.length < 2) return false;

    try {
      const payload = JSON.parse(
        decodeURIComponent(
          atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
            .split("")
            .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join(""),
        ),
      );
      if (!payload.exp) return false;
      const expiresAtMs = payload.exp * 1000;
      return expiresAtMs - Date.now() < 2 * 60 * 1000;
    } catch (_error) {
      return false;
    }
  }, []);

  const ensureFreshTokenForWebSocket = useCallback(async () => {
    if (!shouldRefreshTokenForWebSocket()) return;
    try {
      await authService.refreshToken({ silent: true });
    } catch (_error) {
      // Ignore refresh errors and rely on unauthenticated flow if needed.
    }
  }, [shouldRefreshTokenForWebSocket]);

  const waitForWebSocketOpen = useCallback((websocket, timeoutMs = 5000) => {
    if (!websocket) {
      return Promise.reject(new Error("WebSocket is not initialized"));
    }

    if (websocket.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (websocket.readyState === WebSocket.CLOSING || websocket.readyState === WebSocket.CLOSED) {
      return Promise.reject(new Error("WebSocket is closed"));
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error("WebSocket open timeout"));
      }, timeoutMs);

      const handleOpen = () => {
        cleanup();
        resolve();
      };

      const handleError = () => {
        cleanup();
        reject(new Error("WebSocket open error"));
      };

      const handleClose = () => {
        cleanup();
        reject(new Error("WebSocket closed"));
      };

      const cleanup = () => {
        clearTimeout(timeout);
        websocket.removeEventListener("open", handleOpen);
        websocket.removeEventListener("error", handleError);
        websocket.removeEventListener("close", handleClose);
      };

      websocket.addEventListener("open", handleOpen, { once: true });
      websocket.addEventListener("error", handleError, { once: true });
      websocket.addEventListener("close", handleClose, { once: true });
    });
  }, []);

  const searchWithWebSocketStart = useCallback(
    async (searchQuery, maxResults, requestedSearchMode = searchMode) => {
      const payload = buildSearchRequestPayload({
        searchQuery,
        maxResults,
        language: i18n.language,
        searchMode: requestedSearchMode,
      });
      let websocket = wsRef.current;
      await ensureFreshTokenForWebSocket();
      if (
        websocket &&
        (websocket.readyState === WebSocket.CLOSING ||
          websocket.readyState === WebSocket.CLOSED)
      ) {
        websocket = null;
      }
      if (!websocket) {
        websocket = connectWebSocket();
      }
      await waitForWebSocketOpen(websocket, 5000);
      websocket.send(
        JSON.stringify({
          type: "search_start",
          payload,
        }),
      );
    },
    [
      connectWebSocket,
      ensureFreshTokenForWebSocket,
      i18n.language,
      searchMode,
      waitForWebSocketOpen,
    ],
  );

  const fallbackConfirmViaHttp = useCallback(
    async ({ runId, threadId, confirmed, criteriaSet, userPurpose, queries }) => {
      const authAxios = getAuthenticatedAxios();
      const response = await authAxios.post("/api/v2/search/confirm", {
        run_id: runId,
        thread_id: threadId,
        confirmation: {
          confirmed,
          criteria_set:
            Array.isArray(criteriaSet) && criteriaSet.length > 0 ? criteriaSet : null,
          user_purpose:
            typeof userPurpose === "string" ? userPurpose : null,
          queries:
            Array.isArray(queries) && queries.length > 0 ? queries : null,
        },
      });

      const data = response.data || {};

      if (data.status === "criteria_confirmation_required" && data.criteria_confirmation) {
        applyInterruptPayload({
          runId,
          threadId,
          payload: data.criteria_confirmation,
        });
        return;
      }

      if (data.status === "completed" && data.result) {
        await refreshUsageSnapshot();
        handleWorkflowResult({ payload: data.result, runId });
        return;
      }

      throw new Error(searchErrorFallback);
    },
    [applyInterruptPayload, handleWorkflowResult, refreshUsageSnapshot, searchErrorFallback],
  );

  const submitWorkflowInterrupt = useCallback(
    async ({ runId, threadId, interruptType, confirmed = true, criteriaSet, userPurpose, queries }) => {
      if (!runId || !threadId || !interruptType) {
        throw new Error(searchErrorFallback);
      }
      const lifecycleId = searchLifecycleRef.current;

      if (interruptType === "criteria_confirmation") {
        setIsAwaitingUserPurpose(false);
        setWorkflowWaitingContext("query_clarification");
        upsertCriteriaMessage({
          id: `criteria-${runId}`,
          type: "criteria_confirmation",
          runId,
          threadId,
          pending: true,
          error: null,
        });
      } else if (interruptType === "query_clarification") {
        setIsAwaitingUserPurpose(false);
        setWorkflowWaitingContext(null);
        startStageWithCompletedHistory({
          stage: SearchStages.SEARCHING_PAPERS,
          searchId: runId,
          completedStages: [SearchStages.GENERATING_QUERIES],
        });
        upsertQueryMessage({
          id: `query-${runId}`,
          type: "query_clarification",
          runId,
          threadId,
          pending: true,
          error: null,
        });
      } else {
        const isPurposeCollection = interruptType === "purpose_collection";
        const isPurposeClarification = interruptType === "purpose_clarification";
        setIsAwaitingUserPurpose(isPurposeCollection);
        setWorkflowWaitingContext(
          isPurposeCollection ? "purpose_clarification" : "criteria_confirmation",
        );
        const submittedPurpose =
          typeof userPurpose === "string" ? userPurpose.trim() : "";

        if (isPurposeCollection && submittedPurpose) {
          upsertPurposeReplyMessage({
            id: `purpose-reply-${runId}`,
            type: "purpose_user_reply",
            runId,
            threadId,
            content: submittedPurpose,
          });
        }

        upsertPurposeMessage({
          id: `${interruptType}-${runId}`,
          type: interruptType,
          runId,
          threadId,
          pending: false,
          error: null,
          locked: isPurposeCollection || isPurposeClarification,
          submittedPurpose,
        });
      }
      setIsAwaitingCriteriaConfirmation(true);

      const payload = {
        type: "criteria_confirmation_submit",
        run_id: runId,
        thread_id: threadId,
        payload: {
          confirmed: Boolean(confirmed),
          criteria_set:
            Array.isArray(criteriaSet) && criteriaSet.length > 0 ? criteriaSet : null,
          user_purpose: typeof userPurpose === "string" ? userPurpose : null,
          queries:
            Array.isArray(queries) && queries.length > 0 ? queries : null,
        },
      };

      let websocket = wsRef.current;
      try {
        await ensureFreshTokenForWebSocket();
        if (
          websocket &&
          (websocket.readyState === WebSocket.CLOSING ||
            websocket.readyState === WebSocket.CLOSED)
        ) {
          websocket = null;
        }
        if (!websocket) {
          websocket = connectWebSocket();
        }

        await waitForWebSocketOpen(websocket, 5000);
        const workflowResultPromise = waitForWorkflowResult(
          runId,
          WORKFLOW_CONFIRMATION_TIMEOUT_MS,
        );
        websocket.send(JSON.stringify(payload));
        try {
          await workflowResultPromise;
        } catch (error) {
          const isTimeout =
            error instanceof Error &&
            error.message === WORKFLOW_CONFIRMATION_TIMEOUT_MESSAGE;
          const websocketStillOpen =
            websocket && websocket.readyState === WebSocket.OPEN;
          if (isTimeout && websocketStillOpen) {
            await waitForWorkflowResultWhileSocketAlive(runId, websocket);
            return;
          }
          throw error;
        }
      } catch (_error) {
        if (searchLifecycleRef.current !== lifecycleId) {
          return;
        }
        try {
          await fallbackConfirmViaHttp({
            runId,
            threadId,
            confirmed: Boolean(confirmed),
            criteriaSet,
            userPurpose,
            queries,
          });
        } catch (fallbackError) {
          const message = normalizeEngineErrorMessage(
            fallbackError,
            searchErrorFallback,
          );
          if (interruptType === "criteria_confirmation") {
            upsertCriteriaMessage({
              id: `criteria-${runId}`,
              type: "criteria_confirmation",
              runId,
              threadId,
              pending: false,
              error: message,
            });
          } else if (interruptType === "query_clarification") {
            upsertQueryMessage({
              id: `query-${runId}`,
              type: "query_clarification",
              runId,
              threadId,
              pending: false,
              error: message,
            });
          } else {
            upsertPurposeMessage({
              id: `${interruptType}-${runId}`,
              type: interruptType,
              runId,
              threadId,
              pending: false,
              error: message,
              locked: false,
            });
          }
          setWorkflowWaitingContext(null);
          handleProgressError({ message });
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              type: "error",
              content: message,
            },
          ]);
          throw fallbackError;
        }
      }
    },
    [
      connectWebSocket,
      ensureFreshTokenForWebSocket,
      fallbackConfirmViaHttp,
      handleProgressError,
      searchErrorFallback,
      setIsAwaitingCriteriaConfirmation,
      setIsAwaitingUserPurpose,
      setWorkflowWaitingContext,
      setMessages,
      SearchStages.SEARCHING_PAPERS,
      SearchStages.GENERATING_QUERIES,
      startStageWithCompletedHistory,
      upsertCriteriaMessage,
      upsertPurposeMessage,
      upsertPurposeReplyMessage,
      upsertQueryMessage,
      waitForWebSocketOpen,
      waitForWorkflowResult,
      waitForWorkflowResultWhileSocketAlive,
    ],
  );

  const submitCriteriaConfirmation = useCallback(
    async ({ runId, threadId, confirmed, criteriaSet }) =>
      submitWorkflowInterrupt({
        runId,
        threadId,
        interruptType: "criteria_confirmation",
        confirmed,
        criteriaSet,
      }),
    [submitWorkflowInterrupt],
  );

  const submitQueryClarification = useCallback(
    async ({ runId, threadId, queries }) =>
      submitWorkflowInterrupt({
        runId,
        threadId,
        interruptType: "query_clarification",
        confirmed: true,
        queries,
      }),
    [submitWorkflowInterrupt],
  );

  const resetSearchState = () => {
    searchLifecycleRef.current += 1;
    resetProgress();
    setDiscoveredPapers(null);
    setIsAwaitingCriteriaConfirmation(false);
    setIsAwaitingUserPurpose(false);
    setWorkflowWaitingContext(null);
    setPaperAnalysisGuidance(null);
    guidanceRequestedRunIdsRef.current = new Set();
    workflowContextRef.current = { runId: null, threadId: null };
    pendingSearchQueryRef.current = "";
    pendingSearchModeRef.current = "basic";
    pendingWorkflowRef.current.forEach((pending) => {
      if (pending.timeoutId) {
        clearTimeout(pending.timeoutId);
      }
      pending.reject(new Error("Search session reset"));
    });
    pendingWorkflowRef.current.clear();

    const activeWebSocket = wsRef.current || ws;
    wsRef.current = null;
    if (
      activeWebSocket &&
      activeWebSocket.readyState !== WebSocket.CLOSING &&
      activeWebSocket.readyState !== WebSocket.CLOSED
    ) {
      activeWebSocket.onopen = null;
      activeWebSocket.onmessage = null;
      activeWebSocket.onerror = null;
      activeWebSocket.onclose = null;
      activeWebSocket.close();
    }
    setWs(null);
  };

  const submitSearchPurpose = useCallback(
    async ({ runId, threadId, messageType, userPurpose }) =>
      submitWorkflowInterrupt({
        runId,
        threadId,
        interruptType: messageType,
        confirmed: true,
        userPurpose: typeof userPurpose === "string" ? userPurpose : "",
      }),
    [submitWorkflowInterrupt],
  );

  const handleSearch = async (overrideQuery = null, options = {}) => {
    const lifecycleId = searchLifecycleRef.current;
    const searchQuery = (typeof overrideQuery === "string" ? overrideQuery : null) || query;
    const requestedSearchMode = options.searchMode === "pro" ? "pro" : searchMode;
    if (
      !searchQuery ||
      !searchQuery.trim() ||
      progress.isSearching ||
      isAwaitingCriteriaConfirmation ||
      isAwaitingUserPurpose
    ) {
      return;
    }

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + 1,
        type: "user",
        content: searchQuery,
      },
    ]);

    resetProgress();
    setDiscoveredPapers(null);
    setPaperAnalysisGuidance(null);
    setHasSearchedInSession(true);
    setQuery("");
    setIsAwaitingCriteriaConfirmation(true);
    setIsAwaitingUserPurpose(false);
    pendingSearchQueryRef.current = searchQuery.trim();
    pendingSearchModeRef.current = requestedSearchMode;
    if (requestedSearchMode === "basic") {
      startStage(SearchStages.GENERATING_QUERIES);
    }
    setWorkflowWaitingContext(
      requestedSearchMode === "basic" ? "criteria_confirmation" : "purpose_collection",
    );

    try {
      await searchWithWebSocketStart(searchQuery, 50, requestedSearchMode);
    } catch (error) {
      if (searchLifecycleRef.current !== lifecycleId) {
        return;
      }
      const message = normalizeEngineErrorMessage(error, searchErrorFallback);
      setIsAwaitingCriteriaConfirmation(false);
      setIsAwaitingUserPurpose(false);
      setWorkflowWaitingContext(null);
      setDiscoveredPapers(null);
      pendingSearchQueryRef.current = "";
      pendingSearchModeRef.current = "basic";
      handleProgressError({ message });
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "error",
          content: message,
        },
      ]);
    }
  };

  return {
    query,
    setQuery,
    progress,
    isAwaitingCriteriaConfirmation,
    isAwaitingUserPurpose,
    workflowWaitingContext,
    discoveredPapers,
    paperAnalysisGuidance,
    loadPersistedPaperAnalysisGuidance,
    handleSearch,
    submitSearchPurpose,
    submitCriteriaConfirmation,
    submitQueryClarification,
    resetProgress,
    resetSearchState,
    ws,
    setWs,
    showSurveyModal,
    setShowSurveyModal,
    SearchStages,
    searchMode,
    setSearchMode,
  };
};
